import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors
} from '@dnd-kit/core'
import {
	arrayMove,
	rectSortingStrategy,
	SortableContext,
	useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ReactNode } from 'react'
import { cn } from '#lib/utils'

/**
 * A grid whose items are dragged into a new order. The whole tile is the
 * handle, started only after a short move so clicks on its buttons still work;
 * the caller offers the same reorder as buttons for keyboard users.
 */
function SortableGrid<T extends { id: string }>({
	items,
	onReorder,
	renderItem,
	className,
	itemClassName,
	trailing
}: {
	items: T[]
	/** The ids in the new order, called once when a drag ends elsewhere. */
	onReorder: (ids: string[]) => void
	renderItem: (item: T, index: number) => ReactNode
	className?: string
	/** Grid placement of the tile at an index, e.g. a larger first tile. */
	itemClassName?: (index: number) => string | undefined
	/** Static tiles after the sortable ones, e.g. an "add" tile. */
	trailing?: ReactNode
}) {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 200, tolerance: 6 }
		})
	)

	function handleDragEnd({ active, over }: DragEndEvent) {
		if (!over || active.id === over.id) {
			return
		}
		const ids = items.map((item) => item.id)
		const from = ids.indexOf(String(active.id))
		const to = ids.indexOf(String(over.id))
		onReorder(arrayMove(ids, from, to))
	}

	return (
		<DndContext
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			sensors={sensors}
		>
			<SortableContext
				items={items.map((item) => item.id)}
				strategy={rectSortingStrategy}
			>
				<div className={className}>
					{items.map((item, index) => (
						<SortableTile
							className={itemClassName?.(index)}
							id={item.id}
							key={item.id}
						>
							{renderItem(item, index)}
						</SortableTile>
					))}
					{trailing}
				</div>
			</SortableContext>
		</DndContext>
	)
}

function SortableTile({
	id,
	className,
	children
}: {
	id: string
	className?: string
	children: ReactNode
}) {
	const { setNodeRef, listeners, transform, transition, isDragging } =
		useSortable({ id })
	return (
		<div
			className={cn(
				'min-w-0 cursor-grab touch-manipulation active:cursor-grabbing',
				isDragging && 'relative z-10 opacity-80 shadow-md',
				className
			)}
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			{...listeners}
		>
			{children}
		</div>
	)
}

export { SortableGrid }
